import {
	BoxGeometry,
	DoubleSide,
	ExtrudeGeometry,
	Mesh,
	MeshNormalMaterial
} from 'three';

import type { CutoutInfo } from '../types';
import { cutoutProfileShapes } from '../helpers/cutout-profiles.helpers';
import { createVentProxyMaterial, isVentCutout, VENT_CUTOUT_TYPE } from '../helpers/vent-proxy.helpers';

export interface CreateDebugMeshParams {
	cutout: CutoutInfo;
	debugMode: boolean;
}

/**
 * Create the proxy mesh for a cutout: the debug view's stand-in for the hole, and -- for a vent --
 * the only object a pointer or a bounding box can find the opening by.
 */
export const createDebugMeshCfn = (
	{ cutout, debugMode }: CreateDebugMeshParams
): Mesh => {
	// Every outline the opening cuts, as createCutoutMesh does: a six-slot vent needs six slots of
	// proxy, not the one `points` carries.
	const shapes = cutoutProfileShapes(cutout);
	let geometry;

	if (shapes.length) {
		geometry = new ExtrudeGeometry(shapes, {
			depth: cutout.depth,
			bevelEnabled: false
		});
		geometry.center();
	} else {
		// Default box
		geometry = new BoxGeometry(20, cutout.depth, 16);
	}

	const isVent = isVentCutout(cutout);

	// A vent proxy is shown to the viewer, so it glows; every other proxy is a debug aid, and
	// MeshNormalMaterial colours each face by its normal (as the legacy Haxe debug cutouts did).
	const material = isVent ? createVentProxyMaterial() : new MeshNormalMaterial({ side: DoubleSide });

	const mesh = new Mesh(geometry, material);

	// Apply same transforms as createCutoutMesh
	// Must mirror createCutoutMeshCfn exactly, or the debug box shows a different
	// orientation from the hole actually being cut.
	if (cutout.verticalProfile) {
		mesh.rotation.x = 0;
		mesh.rotation.y = cutout.rotation?.y ?? 0;
	} else {
		mesh.rotation.x = Math.PI / 2;
		if (cutout.rotation?.y) {
			mesh.rotation.z = cutout.rotation.y;
		}
	}

	// Position matching createCutoutMesh (with negation)
	mesh.position.set(
		cutout.position.x,
		cutout.position.y * -1,
		cutout.position.z * -1
	);

	mesh.userData = {
		// A vent proxy answers to the pointer and to the safety legend; every other cutout proxy is
		// a hidden clone of something already selectable, and stays looked-through.
		type: isVent ? VENT_CUTOUT_TYPE : 'appliance-cutout',
		id: cutout.applianceId ? `${cutout.applianceId}-cutout` : `cutout-${cutout.id}`,
		cutoutId: cutout.id,
		islandIndex: cutout.islandPositionIndex,
		islandId: cutout.islandId,
		applianceId: cutout.applianceId
	};

	mesh.name = `cutout-${cutout.applianceId || cutout.id}`;
	mesh.visible = debugMode;

	return mesh;
};
